import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { LogOut, Settings as SettingsIcon, Plus, Sparkles, Tag, Users } from "lucide-react";
import { api } from "@/lib/api";
import TotalsBar from "@/components/TotalsBar";
import UseThisNow from "@/components/UseThisNow";
import AccountsTable from "@/components/AccountsTable";
import ProgramCards from "@/components/ProgramCards";
import EditBalanceModal from "@/components/EditBalanceModal";
import AddProgramModal from "@/components/AddProgramModal";
import MfaPromptModal from "@/components/MfaPromptModal";
import Support from "@/components/Support";
import ExtensionBanner from "@/components/ExtensionBanner";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [programs, setPrograms] = useState([]);
  const [credits, setCredits] = useState([]);
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProgram, setEditProgram] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [mfaProgram, setMfaProgram] = useState(null);
  const [dismissedMfa, setDismissedMfa] = useState(new Set());
  const pollRef = useRef(null);
  const wasSyncingRef = useRef(false);   // for transition-fetch (see below)

  const fetchPrograms = async () => {
    const { data } = await api.get("/programs");
    setPrograms(data);
    return data;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: progs }, { data: creds }, { data: aws }] = await Promise.all([
        api.get("/programs"),
        api.get("/credits"),
        api.get("/awards"),
      ]);
      setPrograms(progs);
      setCredits(creds);
      setAwards(aws);
    } finally {
      setLoading(false);
    }
  };

  // Background refresh — same fetches as load() but WITHOUT flipping the
  // top-level `loading` flag. The flag swaps the entire dashboard for a
  // spinner, which unmounts any open modals and resets their internal
  // state (the AddProgramModal regression where it jumped back to the
  // program picker mid-flow). Use this for any refresh that fires while
  // a modal is open or the user is mid-task.
  const refresh = async () => {
    const [{ data: progs }, { data: creds }, { data: aws }] = await Promise.all([
      api.get("/programs"),
      api.get("/credits"),
      api.get("/awards"),
    ]);
    setPrograms(progs);
    setCredits(creds);
    setAwards(aws);
  };

  // Background poll: while any program has a syncing/pending state, refresh
  // /programs + /awards + /credits every 5s. Awards/credits land slightly
  // after the program row, so polling /programs alone would leave those
  // sections empty until reload — refetch all three together. We also do
  // a single transition-fetch when the last syncing program flips to live.
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    const isSyncing = (p) =>
      p.freshness === "syncing" || p.sync?.state === "pending" || p.sync?.state === null;
    const anySyncing = programs.some(isSyncing);

    if (wasSyncingRef.current && !anySyncing) {
      // We just finished syncing — fetch awards/credits one extra time so
      // late-arriving rows show up without the user reloading.
      Promise.all([api.get("/awards"), api.get("/credits")])
        .then(([{ data: aws }, { data: creds }]) => {
          setAwards(aws);
          setCredits(creds);
        })
        .catch(() => {});
    }
    wasSyncingRef.current = anySyncing;

    if (!anySyncing) return;
    pollRef.current = setInterval(async () => {
      try {
        const [{ data: progs }, { data: creds }, { data: aws }] = await Promise.all([
          api.get("/programs"),
          api.get("/credits"),
          api.get("/awards"),
        ]);
        setPrograms(progs);
        setCredits(creds);
        setAwards(aws);
      } catch {}
    }, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [programs]);

  // Auto-open the MFA modal whenever a program needs an answer (and the user
  // hasn't already dismissed it for this row this session). Opens even if
  // AW didn't return security_questions — the modal falls back to a generic
  // verification-code prompt.
  //
  // Suppressed while AddProgramModal is open: that modal owns its own inline
  // MFA UI during the add-account flow, and stacking a second popup with the
  // same question on top of it is the bug this guard fixes.
  useEffect(() => {
    if (mfaProgram) return;
    if (showAdd) return;
    const candidate = programs.find(
      (p) =>
        (p.sync?.state === "needs_answer" || p.freshness === "needs_answer") &&
        !dismissedMfa.has(p.id)
    );
    if (candidate) setMfaProgram(candidate);
  }, [programs, mfaProgram, dismissedMfa, showAdd]);

  useEffect(() => {
    if (user) load();
  }, [user]);

  const handleSaveBalance = async (id, patch) => {
    const { data } = await api.patch(`/programs/${id}`, patch);
    setPrograms((p) => p.map((x) => (x.id === id ? data : x)));
    setEditProgram(null);
  };

  const handleDeleteProgram = async (id) => {
    await api.delete(`/programs/${id}`);
    setPrograms((p) => p.filter((x) => x.id !== id));
    setEditProgram(null);
  };

  const handleAddProgram = async (body) => {
    const { data } = await api.post("/programs", body);
    setPrograms((p) => [...p, data]);
    setShowAdd(false);
  };

  if (!user || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3 text-neutral-600 text-sm">
          <span className="spinner" /> Loading dashboard…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="dashboard-page">
      {/* HEADER */}
      <header
        className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-black/5"
        data-testid="dashboard-header"
      >
        <div className="max-w-[1180px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center text-white font-bold font-display text-sm">
              R
            </div>
            <div className="flex flex-col">
              <div className="font-display font-semibold tracking-tight leading-none">
                Reservoir <span className="text-neutral-400 font-medium">AI</span>
              </div>
              <div className="text-[11px] text-neutral-500 mt-0.5">
                {user.name || user.email}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              data-testid="plan-trip-btn"
              onClick={() => navigate("/plan")}
              className="btn btn-ghost text-[13px]"
            >
              <Sparkles size={14} />
              Plan a trip
            </button>
            {(user.role === "partner_admin" || user.is_admin) && (
              <button
                data-testid="deals-link"
                onClick={() => navigate("/admin/deals")}
                className="btn btn-ghost text-[13px]"
              >
                <Tag size={14} />
                Deals
              </button>
            )}
            {user.role === "partner_admin" && (
              <button
                data-testid="partner-link"
                onClick={() => navigate("/partner")}
                className="btn btn-ghost text-[13px]"
              >
                <Users size={14} />
                Referral Dashboard
              </button>
            )}
            {user.is_admin && (
              <button
                data-testid="admin-link"
                onClick={() => navigate("/admin")}
                className="btn btn-ghost text-[13px]"
              >
                Admin
              </button>
            )}
            <button
              data-testid="settings-btn"
              onClick={() => navigate("/settings")}
              className="btn btn-ghost text-[13px]"
            >
              <SettingsIcon size={14} />
              Settings
            </button>
            <button
              data-testid="logout-btn"
              onClick={async () => {
                await logout();
                navigate("/login", { replace: true });
              }}
              className="btn btn-ghost text-[13px]"
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1180px] mx-auto px-6 pt-8 fade-in">
        <ExtensionBanner />
        {/* Hero / page title */}
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="section-eyebrow">Dashboard</div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold mt-1 tracking-tight">
              Your points, plainly stated.
            </h1>
            <p className="text-neutral-600 mt-2 text-[14px] max-w-xl">
              All balances on one page. Click any program to edit its balance.
            </p>
          </div>
          <div className="text-[12px] text-neutral-500">
            <span className="pill pill-neutral">Live sync available</span>
          </div>
        </div>

        <TotalsBar programs={programs} credits={credits} awards={awards} />

        {/* First-program onboarding card (only when user has zero programs) */}
        {!loading && programs.length === 0 && (
          <div
            className="card p-7 lg:p-9 mt-8 fade-in"
            data-testid="empty-onboarding-card"
            style={{
              background: "linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 100%)",
              color: "#fafaf7",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="max-w-xl">
                <div className="section-eyebrow" style={{ color: "#a3a3a3" }}>
                  <Sparkles size={11} className="inline mr-1.5" /> Welcome to Reservoir
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-2 leading-tight">
                  Add your first loyalty program.
                </h2>
                <p className="text-neutral-300 text-[14.5px] leading-relaxed mt-3">
                  Pick any program you have points or status with — Hyatt,
                  United, Amex MR, anything. We'll pull it onto your dashboard
                  and the Concierge can start planning trips against your real
                  balances.
                </p>
                <div className="mt-6 flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => setShowAdd(true)}
                    className="btn text-[13.5px] px-5 py-3"
                    style={{ background: "#fff", color: "#0a0a0a" }}
                    data-testid="onboarding-add-first-program-btn"
                  >
                    <Plus size={14} /> Add your first program
                  </button>
                  <span className="text-[12px] text-neutral-500">
                    Takes 30 seconds. You can add more anytime.
                  </span>
                </div>
              </div>
              <div className="text-[11.5px] text-neutral-500 max-w-[200px] hidden md:block">
                Tip: start with the program where you have the most points or
                highest elite status. The Concierge gets smarter the more
                programs you add.
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5">
            <UseThisNow
              credits={credits}
              programs={programs}
              awards={awards}
              onAddCredit={async (body) => {
                const { data } = await api.post("/credits", body);
                setCredits((c) => [...c, data]);
              }}
              onDeleteCredit={async (id) => {
                await api.delete(`/credits/${id}`);
                setCredits((c) => c.filter((x) => x.id !== id));
              }}
              onDeleteAward={async (id) => {
                await api.delete(`/awards/${id}`);
                setAwards((a) => a.filter((x) => x.id !== id));
              }}
            />
          </div>
          <div className="lg:col-span-7">
            <section className="card p-6 h-full flex flex-col" data-testid="travel-portal-cta">
              <div className="section-eyebrow">Travel portal</div>
              <h2 className="font-display text-xl font-extrabold mt-1 tracking-tight">
                Plan a trip with points
              </h2>
              <p className="text-[13px] text-neutral-600 mt-2 flex-1">
                Describe a trip in plain English and chat with the Concierge like
                a travel agent. It reads your real balances, surfaces the best
                award options, and keeps refining the results as you talk —
                cabin, dates, travelers, the works.
              </p>
              <button
                onClick={() => navigate("/plan")}
                className="btn btn-primary mt-4 self-start"
                data-testid="open-trip-planner-btn"
              >
                <Sparkles size={14} /> Start planning
              </button>
            </section>
          </div>
        </div>

        <div className="mt-10">
          <AccountsTable
            programs={programs}
            onEdit={(p) => setEditProgram(p)}
            onAdd={() => setShowAdd(true)}
            onExtSynced={() => { refresh().catch(() => {}); }}
            onMfa={(p) => {
              // Allow re-opening even if previously dismissed
              setDismissedMfa((s) => {
                const n = new Set(s);
                n.delete(p.id);
                return n;
              });
              setMfaProgram(p);
            }}
          />
        </div>

        <div className="mt-10">
          <ProgramCards
            programs={programs}
            awards={awards}
            onEdit={(p) => setEditProgram(p)}
            onAddAward={async (body) => {
              const { data } = await api.post("/awards", body);
              setAwards((a) => [...a, data]);
            }}
            onDeleteAward={async (id) => {
              await api.delete(`/awards/${id}`);
              setAwards((a) => a.filter((x) => x.id !== id));
            }}
          />
        </div>

        <footer className="mt-14 text-[12px] text-neutral-500 leading-relaxed">
          Trip planning runs on the Concierge — our points-obsessed strategist
          with live web search. Balances stay in your tenant; never shared
          across accounts.
        </footer>
      </main>

      <Support />


      {editProgram && (
        <EditBalanceModal
          program={editProgram}
          onClose={() => setEditProgram(null)}
          onSave={handleSaveBalance}
          onDelete={handleDeleteProgram}
        />
      )}
      {showAdd && (
        <AddProgramModal
          onClose={() => setShowAdd(false)}
          onAdd={handleAddProgram}
          onAwConnected={() => { refresh().catch(() => {}); }}
        />
      )}
      {mfaProgram && (
        <MfaPromptModal
          program={mfaProgram}
          onClose={() => {
            setDismissedMfa((s) => new Set(s).add(mfaProgram.id));
            setMfaProgram(null);
          }}
          onSubmitted={() => {
            setMfaProgram(null);
            // Also clear dismissed entry so future MFA on this row re-opens
            setDismissedMfa((s) => {
              const n = new Set(s);
              n.delete(mfaProgram.id);
              return n;
            });
            fetchPrograms().catch(() => {});
          }}
        />
      )}
    </div>
  );
}
